import React from 'react';
import { BarChart, Clock, Award, TrendingUp } from 'lucide-react';
import { Word, Theme } from '../types';

interface StatisticsProps {
  words: Word[];
  theme: Theme;
}

export default function Statistics({ words, theme }: StatisticsProps) {
  const totalWords = words.length;
  const averageMastery = words.reduce((acc, word) => acc + (word.mastery || 0), 0) / totalWords || 0;
  const wordsToReview = words.filter(word => {
    if (!word.nextReview) return false;
    return new Date(word.nextReview) <= new Date();
  }).length;

  const masteryLevels = {
    beginner: words.filter(w => (w.mastery || 0) < 30).length,
    intermediate: words.filter(w => (w.mastery || 0) >= 30 && (w.mastery || 0) < 70).length,
    advanced: words.filter(w => (w.mastery || 0) >= 70).length,
  };

  return (
    <div className={`p-6 rounded-lg shadow-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
      <h2 className={`text-xl font-semibold mb-6 ${
        theme === 'dark' ? 'text-white' : 'text-gray-900'
      }`}>
        Learning Progress
      </h2>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className={`p-4 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'
        }`}>
          <div className="flex flex-col">
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Total Words
            </span>
            <span className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {totalWords}
            </span>
          </div>
        </div>

        <div className={`p-4 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'
        }`}>
          <div className="flex flex-col">
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Due for Review
            </span>
            <span className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {wordsToReview}
            </span>
          </div>
        </div>

        <div className={`p-4 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'
        }`}>
          <div className="flex flex-col">
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Average Mastery
            </span>
            <span className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {Math.round(averageMastery)}%
            </span>
          </div>
        </div>

        <div className={`p-4 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'
        }`}>
          <div className="flex flex-col">
            <span className={`text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Advanced Words
            </span>
            <span className={`text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              {masteryLevels.advanced}
            </span>
          </div>
        </div>
      </div>

      <div className={`p-4 rounded-lg ${
        theme === 'dark' ? 'bg-gray-700' : 'bg-purple-50'
      }`}>
        <h3 className={`text-sm font-medium mb-3 ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>
          Mastery Distribution
        </h3>
        <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full flex">
            <div
              style={{ width: `${(masteryLevels.beginner / totalWords) * 100}%` }}
              className="bg-red-500 transition-all duration-500"
            />
            <div
              style={{ width: `${(masteryLevels.intermediate / totalWords) * 100}%` }}
              className="bg-yellow-500 transition-all duration-500"
            />
            <div
              style={{ width: `${(masteryLevels.advanced / totalWords) * 100}%` }}
              className="bg-green-500 transition-all duration-500"
            />
          </div>
        </div>
        <div className="flex justify-between mt-2">
          <div className="text-sm">
            <span className={`${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              Beginner ({masteryLevels.beginner})
            </span>
          </div>
          <div className="text-sm">
            <span className={`${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
              Intermediate ({masteryLevels.intermediate})
            </span>
          </div>
          <div className="text-sm">
            <span className={`${theme === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
              Advanced ({masteryLevels.advanced})
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}